/**
 * Kategorie-Vorschlaege fuer Schritt 1 (Teamshop / Verein).
 *
 * Zweck: Der Nutzer tippt den Vereinsnamen in das Name-Feld; daraus werden passende
 * Plenty-Kategorien samt ID vorgeschlagen, damit die Kategorie-ID nicht mehr von Hand
 * herausgesucht werden muss. Kategoriename und Teamshop-Name sind fachlich identisch
 * (User-Entscheidung 2026-08-19).
 *
 * KEIN React-/Netzwerk-Import — reine Funktionen, per Vitest isoliert testbar (analog
 * `categoryFilter.ts`, das den Typeahead im PoolManager bedient). Datenquelle ist die
 * bereits vorhandene Liste aus `GET /pool/categories` ({ categoryId, label }), wobei
 * `label` der volle Pfad ist ("Teamshops > SV Ohrbeck").
 *
 * Bewusst NICHT hart auf den Teamshops-Unterbaum gefiltert: Treffer darin werden nur nach
 * oben sortiert, Treffer ausserhalb bleiben waehlbar (User-Entscheidung 2026-08-19). Ein
 * hartes Filter wuerde bei einer Umbenennung des Kategoriebaums stillschweigend nichts
 * mehr finden.
 */

/** Eintrag wie von GET /pool/categories geliefert (deckungsgleich zu CategoryOption). */
export interface CategoryListItem {
  categoryId: number;
  label: string;
}

export interface CategorySuggestion {
  categoryId: number;
  /** Voller Pfad, z.B. "Teamshops > SV Ohrbeck" — im Vorschlag anzeigen, damit klar ist was gewaehlt wird. */
  label: string;
  /** Letztes Pfadsegment, z.B. "SV Ohrbeck" — das ist der eigentliche Kategoriename. */
  leaf: string;
  /** true, wenn der Eintrag im "Teamshops"-Unterbaum haengt (wird zuerst einsortiert). */
  inTeamshops: boolean;
}

/** Wurzelkategorie der Vereins-Teamshops (Plenty-ID 268, 267 Kinder — Stand 2026-08-19). */
export const TEAMSHOP_ROOT_LABEL = 'Teamshops';

/** Trennzeichen der Pfad-Labels aus buildCategoryList() (backend/src/categoryLogic.ts). */
export const PATH_SEPARATOR = ' > ';

/** Ab dieser Query-Laenge wird vorgeschlagen — darunter waere die Liste reines Rauschen. */
export const MIN_QUERY_LENGTH = 2;

/** Deckel fuer die Dropdown-Laenge. */
export const MAX_SUGGESTIONS = 8;

const UMLAUTS: Array<[RegExp, string]> = [
  [/ä/g, 'ae'], [/ö/g, 'oe'], [/ü/g, 'ue'], [/ß/g, 'ss'],
];

/**
 * Vergleichsform eines Namens: kleingeschrieben, Umlaute ausgeschrieben (ä→ae, ß→ss),
 * restliche Diakritika entfernt (é→e), alles Nicht-Alphanumerische zu Leerzeichen
 * (Bindestriche, Punkte, Schraegstriche), Mehrfach-Leerzeichen zusammengefasst.
 *
 * Damit matcht "SV Grossen-Linden" auf "TSV Großen-Linden" und "FC St.Pauli" auf "FC St Pauli".
 */
export function normalizeForMatch(input: string): string {
  let out = input.toLowerCase();
  for (const [pattern, replacement] of UMLAUTS) {
    out = out.replace(pattern, replacement);
  }
  return out
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // verbleibende Akzente entfernen
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Letztes Segment eines Pfad-Labels ("Teamshops > SV Ohrbeck" -> "SV Ohrbeck"). */
export function leafName(label: string): string {
  const parts = label.split(PATH_SEPARATOR);
  return (parts[parts.length - 1] ?? '').trim();
}

/** true, wenn `label` der Wurzel entspricht oder darunter haengt. */
export function isInSubtree(label: string, rootLabel: string): boolean {
  const trimmed = label.trim();
  return trimmed === rootLabel || trimmed.startsWith(rootLabel + PATH_SEPARATOR);
}

/**
 * Trefferguete, kleiner ist besser. `null` = kein Treffer.
 *   0 exakter Kategoriename        ("sv ohrbeck"       -> "SV Ohrbeck")
 *   1 Kategoriename beginnt damit  ("sv ohr"           -> "SV Ohrbeck")
 *   2 alle Suchwoerter enthalten   ("linden grossen"   -> "TSV Großen-Linden")
 *   3 Kategoriename enthaelt es    ("ohrbeck"          -> "SV Ohrbeck")
 *   4 nur der Pfad enthaelt es     ("teamshops"        -> "Teamshops > SV Ohrbeck")
 */
export function matchRank(leaf: string, path: string, query: string): number | null {
  const q = normalizeForMatch(query);
  if (q === '') return null;

  const leafNorm = normalizeForMatch(leaf);
  const pathNorm = normalizeForMatch(path);

  if (leafNorm === q) return 0;
  if (leafNorm.startsWith(q)) return 1;

  const tokens = q.split(' ').filter(t => t !== '');
  if (tokens.length > 1 && tokens.every(t => leafNorm.includes(t))) return 2;

  if (leafNorm.includes(q)) return 3;
  if (pathNorm.includes(q)) return 4;
  return null;
}

export interface SuggestOptions {
  rootLabel?: string;
  limit?: number;
  minQueryLength?: number;
}

/**
 * Liefert die besten Kategorie-Vorschlaege zu einem eingegebenen Vereinsnamen.
 *
 * Sortierung: Teamshops-Unterbaum zuerst, darin nach Trefferguete, bei Gleichstand
 * alphabetisch (localeCompare 'de'). Query unter `minQueryLength` -> [] (leere Dropdown
 * statt 528 Eintraege). Reihenfolge ist deterministisch — gleiche Eingabe, gleiche Liste.
 */
export function suggestCategories(
  list: CategoryListItem[],
  query: string,
  options: SuggestOptions = {},
): CategorySuggestion[] {
  const rootLabel = options.rootLabel ?? TEAMSHOP_ROOT_LABEL;
  const limit = options.limit ?? MAX_SUGGESTIONS;
  const minQueryLength = options.minQueryLength ?? MIN_QUERY_LENGTH;

  if (query.trim().length < minQueryLength) return [];
  if (normalizeForMatch(query) === '') return []; // z.B. "--" -> sonst matcht alles

  const scored: Array<{ suggestion: CategorySuggestion; rank: number }> = [];

  for (const entry of list) {
    const label = entry?.label ?? '';
    const leaf = leafName(label);
    const rank = matchRank(leaf, label, query);
    if (rank === null) continue;

    scored.push({
      rank,
      suggestion: {
        categoryId: entry.categoryId,
        label,
        leaf,
        inTeamshops: isInSubtree(label, rootLabel),
      },
    });
  }

  scored.sort((a, b) => {
    if (a.suggestion.inTeamshops !== b.suggestion.inTeamshops) {
      return a.suggestion.inTeamshops ? -1 : 1; // Teamshops-Unterbaum zuerst
    }
    if (a.rank !== b.rank) return a.rank - b.rank;
    return a.suggestion.leaf.localeCompare(b.suggestion.leaf, 'de');
  });

  return scored.slice(0, limit).map(s => s.suggestion);
}

/**
 * Loest eine manuell eingetippte Kategorie-ID gegen die geladene Liste auf — fuer die
 * Bestaetigungsanzeige neben dem ID-Feld.
 *
 * `null` bedeutet AUSDRUECKLICH NICHT "ungueltig": die Registry ist ein Snapshot (<=24h),
 * eine heute in Plenty angelegte Kategorie fehlt darin. Aufrufer duerfen daraus keinen
 * Blocker machen, nur einen Hinweis.
 */
export function findCategoryById(list: CategoryListItem[], categoryId: string | number): CategorySuggestion | null {
  const id = typeof categoryId === 'number' ? categoryId : Number(String(categoryId).trim());
  if (!Number.isFinite(id) || String(categoryId).trim() === '') return null;

  const hit = list.find(entry => entry.categoryId === id);
  if (!hit) return null;

  return {
    categoryId: hit.categoryId,
    label: hit.label,
    leaf: leafName(hit.label),
    inTeamshops: isInSubtree(hit.label, TEAMSHOP_ROOT_LABEL),
  };
}

/**
 * true, wenn der getippte Teamshop-Name inhaltlich vom Kategorienamen abweicht (Basis fuer
 * den "Namen uebernehmen"-Hinweis). Vergleich ueber die Normalform, damit reine
 * Schreibweisen-Unterschiede (Umlaut/Bindestrich/Doppelleerzeichen) keinen Hinweis erzeugen.
 */
export function namesDiffer(typedName: string, categoryLeaf: string): boolean {
  return normalizeForMatch(typedName) !== normalizeForMatch(categoryLeaf);
}
